import * as React from "react";
import s from "./loading.scss";
import Icon from "#components/icon";

interface LoadingProps {
	year?: number;
	onComplete?: () => void;
	isLeaving?: boolean;
	isLoading?: boolean;
	error?: Error | null;
	hasNoData?: boolean;
	onRetry?: () => void;
}

const STEP_DURATION = 2000;

function getSteps(year: number) {
	return [
		{
			title: String(year),
			subtitle: "Preparando sua retrospectiva...",
		},
		{
			title: "Seus dados",
			subtitle: "Analisando seus números do ano...",
		},
		{
			title: "Quase lá!",
			subtitle: "Montando sua experiência épica...",
		},
	];
}

export function Loading({
	year = 2025,
	onComplete,
	isLeaving = false,
	isLoading = false,
	error = null,
	hasNoData = false,
	onRetry,
}: LoadingProps) {
	const steps = React.useMemo(() => getSteps(year), [year]);
	const [currentStep, setCurrentStep] = React.useState(0);
	const [isExiting, setIsExiting] = React.useState(false);
	const [hasReachedLastStep, setHasReachedLastStep] = React.useState(false);
	const lastStepStartTimeRef = React.useRef<number | null>(null);
	const [hasWaitedMinimumTime, setHasWaitedMinimumTime] = React.useState(false);
	const wasShowingErrorRef = React.useRef(false);
	const hasResetRef = React.useRef(false);

	React.useEffect(() => {
		const isShowingError =
			(!!error || hasNoData) && hasReachedLastStep && hasWaitedMinimumTime && !isLoading;
		wasShowingErrorRef.current = isShowingError;
	}, [error, hasNoData, hasReachedLastStep, hasWaitedMinimumTime, isLoading]);

	React.useEffect(() => {
		if (
			wasShowingErrorRef.current &&
			isLoading &&
			!error &&
			!hasNoData &&
			!hasResetRef.current
		) {
			hasResetRef.current = true;
			setCurrentStep(0);
			setHasReachedLastStep(false);
			setIsExiting(false);
			setHasWaitedMinimumTime(false);
			lastStepStartTimeRef.current = null;

			setTimeout(() => {
				hasResetRef.current = false;
			}, 100);
		}
	}, [isLoading, error, hasNoData]);

	React.useEffect(() => {
		if (hasResetRef.current) return;

		if (currentStep < steps.length - 1) {
			const timer = setTimeout(() => {
				setIsExiting(true);
				setTimeout(() => {
					setCurrentStep(prev => prev + 1);
					setIsExiting(false);
				}, 300);
			}, STEP_DURATION);

			return () => clearTimeout(timer);
		} else if (currentStep === steps.length - 1 && !hasReachedLastStep) {
			setHasReachedLastStep(true);
			lastStepStartTimeRef.current = Date.now();
		}
	}, [currentStep, steps.length, hasReachedLastStep]);

	React.useEffect(() => {
		if (!hasReachedLastStep || hasWaitedMinimumTime) return;

		const timer = setTimeout(() => {
			setHasWaitedMinimumTime(true);
		}, STEP_DURATION);

		return () => clearTimeout(timer);
	}, [hasReachedLastStep, hasWaitedMinimumTime]);

	React.useEffect(() => {
		if (!hasReachedLastStep || !hasWaitedMinimumTime) return;

		if (isLoading) return;

		if (error) return;

		if (hasNoData) return;

		onComplete?.();
	}, [hasReachedLastStep, hasWaitedMinimumTime, isLoading, error, hasNoData, onComplete]);

	const showErrorStep =
		(error || hasNoData) && hasReachedLastStep && hasWaitedMinimumTime && !isLoading;

	if (showErrorStep) {
		return (
			<div className={`${s.loading} ${isLeaving ? s.leaving : ""}`}>
				<div className={s.backgroundCircles}>
					<div className={s.circle} />
					<div className={s.circle} />
					<div className={s.circle} />
				</div>

				<div className={`${s.content} ${s.entering}`}>
					<h1 className={s.title}>Ops!</h1>
					<p className={s.subtitle}>
						{hasNoData
							? "Parece que os dados ainda não estão disponíveis"
							: "Algo não saiu como esperado ao carregar sua retrospectiva"}
					</p>
					{onRetry && !hasNoData && (
						<button className={s.retryButton} onClick={onRetry} type="button">
							<Icon icon="ri-refresh-line" />
							Tentar novamente
						</button>
					)}
				</div>
			</div>
		);
	}

	const step = steps[currentStep];

	return (
		<div className={`${s.loading} ${isLeaving ? s.leaving : ""}`}>
			<div className={s.backgroundCircles}>
				<div className={s.circle} />
				<div className={s.circle} />
				<div className={s.circle} />
			</div>

			<div className={`${s.content} ${isExiting ? s.exiting : s.entering}`}>
				<h1 className={s.title}>{step.title}</h1>
				<p className={s.subtitle}>{step.subtitle}</p>
			</div>
		</div>
	);
}
